import {
  lazy,
  PropsWithChildren,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import DakotaTestPrep from "./components/DakotaTestPrep";
import ExamScreen from "./components/test/ExamScreen";
import ExamResults from "./components/results/ExamResults";
import ExamTypeSelection from "./components/ExamTypeSelection";
import { useRootStore } from "./state/RootStore";
import Home from "./components/home";
import PastExamsResults from "./components/results/PastExamResults";
import Profile from "./components/profile/Profile";
import CreateProfile from "./components/profile/CreateProfile";
import EditProfileInfoRefactored from "./components/profile/EditProfileInfoRefactored";
import ExamResultStandalone from "./components/results/ExamResultStandalone";
import { useAuth, useUser } from "@clerk/clerk-react";
import { APP_TO_BUILD, PracticeTestType } from "../appsConfig";
import Paywall from "./components/payment/Paywall";
import PaymentSuccess from "./components/payment/PaymentSuccess";
import ManagePayments from "./components/profile/ManagePayments";
import NotificationSettings from "./components/profile/NotificationSettings";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/toaster";
import SplashScreen from "./components/SplashScreen";
import { LoadFailure } from "./components/common/LoadFailure";
import { useIdentityTransition } from "./hooks/useIdentityTransition";
import { showToastFromKey } from "./lib/toast";
import { IS_E2E, setTestNavigate } from "./lib/isE2E";
import RedeemSuccessPage from "./components/redemption/RedeemSuccessPage";
import PendingAccessCodeStatusDialog, {
  type PendingAccessCodeStatus,
} from "./components/redemption/PendingAccessCodeStatusDialog";
import RedeemCodeModal from "./components/redemption/RedeemCodeModal";
import { RedemptionPresentationProvider } from "./components/redemption/RedemptionPresentationContext";
import { refreshClerkSession } from "./lib/useAuthedFetch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { DialogPortal } from "@radix-ui/react-dialog";
import { useToggle } from "react-use";
import dakotaTired from "@/assets/images/dakota-tired.webp";

import posthog, { captureEvent } from "./utils/posthog";
import SignUpPage from "./components/auth/SignUp";
import { SignInPage } from "./components/auth/SignIn";
import { reaction } from "mobx";
import * as Sentry from "@sentry/react";
import { checkFirebaseSession, registerSession } from "./api/fetch";
import { useLogout } from "./hooks/useLogout";
import {
  capturePendingReferral,
  usePendingReferral,
} from "./hooks/usePendingReferral";
import { useIsMobileWeb } from "./hooks/useIsMobileWeb";
import { isMobileWebAllowedPath } from "@/utils/mobileWebRoutes";
import { DOWNLOAD_APP_URL } from "@/utils/downloadApp";
import {
  usePendingAccessCode,
  type PendingAccessCodePresentation,
} from "@/hooks/usePendingAccessCode";
import { clearPendingAccessCode } from "@/utils/orgPurchases/pendingAccessCode";
import {
  hasBlockingRedemptionPresentation,
  shouldDeferRedemptionAttempt,
  shouldDeferRedemptionError,
  shouldDeferRedemptionStatus,
} from "@/utils/orgPurchases/redemptionPolicy";
import { getOrgRedeemSuccessRoute } from "@/utils/orgPurchases/redemptionRouting";
import { canShowChangeExamType } from "@/utils/examTypeNavigation";
import { resolveProtectedRouteAuthState } from "./utils/protectedRouteAuth";
import {
  PAID_PROFILE_COMPLETION_ROUTE,
  requiresPaidProfileCompletion,
  shouldRedirectToPaidProfileCompletion,
} from "./utils/paidProfileCompletion";
import {
  clearPendingSignUpExamSelection,
  isSignUpAuthenticationReturn,
  readPendingSignUpExamSelection,
  resolvePostAuthExamSelection,
  type PendingSignUpExamSelection,
} from "./utils/pendingSignUpExamSelection";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Multi-session eviction check. Was 5s, which cost ~720 backend GETs per hour per visible
// tab — plus a forced Clerk mint per tick and a second mint every other tick, ~1,800
// requests/hour in total. Eviction is not time-critical: a minute of latency is fine, and
// the check also runs whenever the tab becomes visible.
const SESSION_CHECK_INTERVAL_MS = 60_000;

// Deferred: admin, org-purchase, referral and AI-tutor routes are reached by a small
// fraction of sessions, but were shipped in the single entry chunk every student
// downloaded before they could see anything. All render inside the Suspense boundary
// below.
const ForcePurchaseState = lazy(() => import("./components/ForcePurchaseState"));
const DistributionPage = lazy(() => import("./components/orgPurchases/DistributionPage"));
const OrganizationExamPage = lazy(() => import("./components/orgPurchases/OrganizationExamPage"));
const RedeemOrgLinkPage = lazy(() => import("./components/orgPurchases/RedeemOrgLinkPage"));
const ReferralLandingPage = lazy(() => import("./components/referral/ReferralLandingPage"));
const ReferralScreen = lazy(() => import("./components/referral/ReferralScreen"));
const PerksPage = lazy(() => import("./components/perks/PerksPage"));
const AIChat = lazy(() => import("./components/ai-chat/AIChat"));
const QuestionGuidesPage = lazy(
  () => import("./components/question-guides/QuestionGuidesPage"),
);

const ExamOutcomePage = lazy(
  () => import("./components/email/ExamOutcomePage"),
);

const UnsubscribePage = lazy(
  () => import("./components/email/UnsubscribePage"),
);

const ProtectedRoute = observer(({ children }: PropsWithChildren) => {
  const store = useRootStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const [params, setSearchParams] = useSearchParams();
  const justAuthenticatedRef = useRef(false);

  const stateSelected = store.mainAppStore.selectedStateCode;
  const profileFetched = store.mainAppStore.profileSaved;
  const processingAuthRef = useRef(false);
  const pendingSignUpSelectionRef =
    useRef<PendingSignUpExamSelection | null>(null);

  const reconcilePendingSignUpSelection = useCallback(async () => {
    const resolution = resolvePostAuthExamSelection(
      store.mainAppStore.selectedStateCode,
      pendingSignUpSelectionRef.current,
      !store.mainAppStore.profileError,
      store.mainAppStore.profileHasExplicitStateSelection,
    );
    if (resolution.source === "unknown") return false;

    if (resolution.source === "pending") {
      store.mainAppStore.selectedCodebookYear = resolution.selection.codebookYear;
      try {
        await store.mainAppStore.setSelectedStateCode(
          resolution.selection.stateCode,
          false,
          { clearCodebookYear: false },
        );
        if (resolution.selection.codebookYear != null) {
          await store.mainAppStore.setSelectedCodebookYear(
            resolution.selection.codebookYear,
          );
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            area: "authentication",
            phase: "restore_exam_selection",
          },
        });
        return false;
      }
    }

    clearPendingSignUpExamSelection();
    pendingSignUpSelectionRef.current = null;
    return true;
  }, [store.mainAppStore]);

  // Handle sign in/sign up redirect and session refresh
  useEffect(() => {
    const afterSignIn = params.get("afterSignIn");
    const afterSignUp = params.get("afterSignUp");
    const shouldResumeSignUpSelection = isSignUpAuthenticationReturn({
      afterSignIn: Boolean(afterSignIn),
      afterSignUp: Boolean(afterSignUp),
      fromSignUp: params.get("fromSignUp") === "true",
    });

    if ((afterSignIn || afterSignUp) && !processingAuthRef.current) {
      if (shouldResumeSignUpSelection) {
        pendingSignUpSelectionRef.current =
          readPendingSignUpExamSelection(APP_TO_BUILD) ||
          (store.mainAppStore.selectedStateCode
            ? {
                codebookYear: store.mainAppStore.selectedCodebookYear,
                stateCode: store.mainAppStore.selectedStateCode,
              }
            : null);
      }

      // Prevent multiple executions
      processingAuthRef.current = true;

      // Mark that we just authenticated
      justAuthenticatedRef.current = true;

      store.mainAppStore.storeInitialized = false;
      setSearchParams([]);
      refreshClerkSession().then(async () => {
        await store.reset();
        if (shouldResumeSignUpSelection) {
          await reconcilePendingSignUpSelection();
        }
        // Keep processingAuthRef true until navigation happens
      });
      return;
    }

    if (
      processingAuthRef.current &&
      pendingSignUpSelectionRef.current &&
      profileFetched &&
      !store.mainAppStore.profileError
    ) {
      void reconcilePendingSignUpSelection();
      return;
    }

    if (
      profileFetched &&
      !stateSelected &&
      !pendingSignUpSelectionRef.current?.stateCode &&
      PracticeTestType.byState &&
      !requiresPaidProfileCompletion(
        store.mainAppStore.newPurchaseState?.purchaseAccess,
        store.mainAppStore.profilePersistedComplete,
      ) &&
      location.pathname !== "/exam-selection-standalone"
    ) {
      navigate("/exam-selection-standalone", { replace: true });
      return;
    }
  }, [
    isLoaded,
    profileFetched,
    stateSelected,
    isSignedIn,
    navigate,
    store.mainAppStore.profileEmail,
    store.mainAppStore.profilePersistedComplete,
    store.mainAppStore.newPurchaseState,
    location.pathname,
    location.state,
    reconcilePendingSignUpSelection,
    store.mainAppStore.profileError,
  ]);

  // Track authentication success after profile is fetched
  // Use MobX reaction to properly watch observable changes
  useEffect(() => {
    const dispose = reaction(
      () => ({
        profileFetched: store.mainAppStore.profileSaved,
        storeInitialized: store.mainAppStore.storeInitialized,
        profileName: store.mainAppStore.profileName,
        profileEmail: store.mainAppStore.profileEmail,
      }),
      ({ profileFetched, storeInitialized, profileName }) => {
        if (
          justAuthenticatedRef.current &&
          profileFetched &&
          storeInitialized
        ) {
          if (!store.mainAppStore.profileName && user) {
            let firstName = user.firstName ?? "";
            let lastName = user.lastName ?? "";

            const appleAccount = user.externalAccounts?.find(
              (acc) => acc.provider === "apple",
            );
            if (appleAccount) {
              firstName = firstName || appleAccount.firstName || "";
              lastName = lastName || appleAccount.lastName || "";
            }

            const fullName = `${firstName} ${lastName}`.trim();
            if (fullName && !store.mainAppStore.profileName) {
              store.mainAppStore.setProfileName(fullName);
            }
          }

          // Fire authentication success event
          captureEvent("authentication_success", {
            user_authentication_status: !profileName ? "new" : "existing",
          });

          // Set sign-up attribution for new users
          if (!profileName) {
            posthog.capture("$set", {
              $set: {
                sign_up_attribution: `${APP_TO_BUILD} - mobile`,
              },
            });
          }

          // Reset the flag so events don't fire again
          justAuthenticatedRef.current = false;
        }
      },
      { fireImmediately: true },
    );

    return () => dispose();
  }, [user, navigate]);

  const authState = resolveProtectedRouteAuthState(isLoaded, isSignedIn);
  if (authState === "loading") {
    return <SplashScreen />;
  }
  if (authState === "signed-out") {
    return <Navigate to="/sign-in" replace />;
  }

  if (!store.mainAppStore.storeInitialized || store.questionsStore.isLoading) {
    return <SplashScreen />;
  }

  // The question bank is the one dependency the app cannot degrade past. With an empty
  // map every study and exam screen renders as though the content simply does not
  // exist, which reads as data loss rather than a failed fetch. `loadingError` was
  // being recorded here and read by nothing, so the failure was invisible.
  if (store.questionsStore.loadingError) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <LoadFailure
          onRetry={store.questionsStore.retryLoadQuestions}
          titleKey="errors.question_bank_failed_title"
          bodyKey="errors.question_bank_failed_body"
        />
      </div>
    );
  }

  if (
    pendingSignUpSelectionRef.current?.stateCode &&
    store.mainAppStore.profileError
  ) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <LoadFailure onRetry={() => void store.mainAppStore.retryProfileLoad()} />
      </div>
    );
  }

  if (
    shouldRedirectToPaidProfileCompletion({
      isSignedIn: true,
      profileFetched: Boolean(profileFetched),
      purchaseAccess: store.mainAppStore.newPurchaseState?.purchaseAccess,
      isProfileComplete: store.mainAppStore.profilePersistedComplete,
      pathname: location.pathname,
    })
  ) {
    return <Navigate to={PAID_PROFILE_COMPLETION_ROUTE} replace />;
  }

  return <>{children}</>;
});

const MobileWarning = () => {
  const { t } = useTranslation();
  return (
    <div className="w-full min-h-screen gap-6 flex flex-col items-center justify-center bg-gray-50 p-6">
      <img
        src={dakotaTired}
        alt={t("auth.dakota_prep_pro_character_alt")}
        className="w-[273px] h-[273px]"
      />
      <p className="font-inter text-xl text-center">
        {t("common.mobile_warning")}
      </p>
      <a
        href={DOWNLOAD_APP_URL}
        className="w-full px-8 py-4 bg-[#5db075] rounded-full text-white text-base font-inter font-semibold hover:bg-[#4c9d62] transition-colors text-center"
      >
        {t("common.download_app")}
      </a>
    </div>
  );
};

/**
 * E2E-only: exposes react-router's navigate to window.__TEST__ for instant SPA
 * navigation from Playwright. Mounted only when IS_E2E; renders nothing.
 */
function TestNavBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setTestNavigate((path: string) => navigate(path));
  }, [navigate]);
  return null;
}

function App() {
  const { t } = useTranslation();
  const { isSignedIn } = useAuth();
  const store = useRootStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { handleSignOut } = useLogout();

  // Must run above the routes: it discards the previous account's browser state, and
  // the logout sweep does not cover session expiry or a sign-out from another tab.
  useIdentityTransition();

  const [signedOutModalOpen, toggleSignedOutModalOpen] = useToggle(false);
  // Set when the student closes the redeeming dialog. The run continues (it may still
  // succeed), but it must not re-open a modal or navigate them anywhere afterwards.
  const redemptionAbandonedRef = useRef(false);
  // Guards against overlapping session checks, and rate-limits the visibility-triggered one.
  const lastRedemptionIdentityRef = useRef<string | null>(null);
  const sessionCheckInFlightRef = useRef(false);
  const lastSessionCheckAtRef = useRef(0);

  // Resets the dismissal only for a genuinely NEW code.
  //
  // NOT on every "redeeming" emission: a single run emits that twice — once when the
  // pending code is found and again after waitForStoreReady, which can take up to 30s.
  // Keying on the emission would let the second one silently un-dismiss a dialog the
  // student closed during the wait, AND re-open it on top of them.
  const handleRedemptionPresentationChange = useCallback(
    (presentation: PendingAccessCodePresentation) => {
      if (presentation.status === "redeeming") {
        const identity = `${presentation.pending.code}:${presentation.pending.examType}`;
        if (lastRedemptionIdentityRef.current !== identity) {
          lastRedemptionIdentityRef.current = identity;
          redemptionAbandonedRef.current = false;
        }
      }

      // Once dismissed, NOTHING from that run may re-open a dialog. The check has to
      // cover every status, not just "redeeming": the run continues after dismissal and
      // always ends by emitting "success" or "error", so guarding only the spinner let
      // the terminal dialog pop back on top of a student who had closed it and moved on.
      //
      // Success is still worth telling them about — their access really did change — so
      // it degrades to a toast rather than being dropped entirely. An error needs no
      // toast: dismissing means they stopped waiting, and the code is untouched, so the
      // actionable path is simply redeeming it again.
      if (redemptionAbandonedRef.current && presentation.status !== "idle") {
        if (presentation.status === "success") {
          showToastFromKey({
            titleKey: "redeem.status_success_title",
            descriptionKey: "redeem.status_success_body",
          });
        }
        return;
      }

      setPendingAccessCodePresentation(presentation);
    },
    [],
  );
  const [pendingAccessCodePresentation, setPendingAccessCodePresentation] =
    useState<PendingAccessCodePresentation>({ status: "idle" });
  const [redemptionPresentationBlockers, setRedemptionPresentationBlockers] =
    useState<Set<string>>(() => new Set());
  const isMobileWeb = useIsMobileWeb();

  // Capture ?ref= on ANY route (must stay top-level — /sign-up?ref=X is a
  // public route outside ProtectedRoute), then redeem post-auth. The hook
  // no-ops until Clerk reports isSignedIn and a pending code exists.
  useEffect(() => {
    capturePendingReferral(searchParams.get("ref"));
  }, [searchParams]);
  usePendingReferral();

  useEffect(() => {
    if (!isSignedIn) {
      setPendingAccessCodePresentation({ status: "idle" });
      setRedemptionPresentationBlockers(new Set());
    }
  }, [isSignedIn]);

  const setRedemptionPresentationBlocker = useCallback(
    (key: string, active: boolean) => {
      setRedemptionPresentationBlockers((current) => {
        const alreadyActive = current.has(key);
        if (alreadyActive === active) return current;

        const next = new Set(current);
        if (active) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [],
  );

  const storeReady = Boolean(
    store.mainAppStore.storeInitialized && store.mainAppStore.profileSaved,
  );
  const stateSelectionPending = Boolean(
    store.mainAppStore.profileSaved &&
    PracticeTestType.byState &&
    !store.mainAppStore.selectedStateCode,
  );
  const profileCompletionPending = requiresPaidProfileCompletion(
    store.mainAppStore.newPurchaseState?.purchaseAccess,
    store.mainAppStore.profilePersistedComplete,
  );
  // The store flag covers the interval before desktop MainStudy mounts. Once
  // mounted, its local coachmark state registers the live blocker. Mobile never
  // mounts MainStudy, so the store-only fallback is intentionally desktop-only.
  const blockingPresentationActive = hasBlockingRedemptionPresentation({
    isMobileWeb,
    registeredBlockerCount: redemptionPresentationBlockers.size,
    showStudyCoachmark: store.mainAppStore.showCoachmark === true,
    signedOutModalOpen,
  });

  const isRedemptionStatusDeferred = shouldDeferRedemptionStatus({
    pathname: location.pathname,
    blockingPresentationActive,
    storeReady,
    stateSelectionPending,
  });
  const isRedemptionErrorDeferred = shouldDeferRedemptionError({
    pathname: location.pathname,
    blockingPresentationActive,
    storeReady,
    profileCompletionPending,
    stateSelectionPending,
  });
  const isRedemptionAttemptDeferred = shouldDeferRedemptionAttempt({
    pathname: location.pathname,
    blockingPresentationActive,
    storeReady,
    profileCompletionPending,
    stateSelectionPending,
  });

  usePendingAccessCode({
    isAbandoned: () => redemptionAbandonedRef.current,
    canAttempt: !isRedemptionAttemptDeferred,
    isMobileWeb,
    onPresentationChange: handleRedemptionPresentationChange,
  });

  const routeOwnsRedemptionSuccess =
    location.pathname === "/redeem-success" ||
    location.pathname === "/payment-success";

  const statusDialogStatus: PendingAccessCodeStatus =
    pendingAccessCodePresentation.status === "success" &&
    !isRedemptionStatusDeferred &&
    !routeOwnsRedemptionSuccess
      ? "success"
      : pendingAccessCodePresentation.status === "redeeming" &&
          !isRedemptionStatusDeferred
        ? "redeeming"
        : "idle";

  const handleRootCodeRedeemed = useCallback(async () => {
    if (pendingAccessCodePresentation.status !== "error") return false;

    const { pending } = pendingAccessCodePresentation;
    clearPendingAccessCode(pending);
    try {
      await store.mainAppStore.checkPurchaseState();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { area: "org_access_code", phase: "manual_refresh_after_success" },
      });
    }
    setPendingAccessCodePresentation({ status: "success", pending });
    navigate(
      getOrgRedeemSuccessRoute(store.mainAppStore.isProfileComplete, {
        isMobileWeb,
      }),
      { replace: true },
    );
    return true;
  }, [
    isMobileWeb,
    navigate,
    pendingAccessCodePresentation,
    store.mainAppStore,
  ]);

  const onLogout = useCallback(async () => {
    await handleSignOut();
    toggleSignedOutModalOpen(false);
  }, []);

  useEffect(() => {
    captureEvent("page_view", {
      path: location.pathname,
    });
  }, [location]);

  useEffect(() => {
    if (isSignedIn) {
      // Register session for per-app multi-session support (fire-and-forget)
      registerSession().catch((error) => Sentry.captureException(error));
    }
  }, [isSignedIn]);

  useEffect(() => {
    // Set by this effect's cleanup, which React runs on sign-out (isSignedIn is a dep)
    // and on unmount. Re-reading `isSignedIn` after an await would NOT work: it is
    // captured per effect run, so an in-flight check still sees the stale `true`.
    let cancelled = false;

    const checkSession = async () => {
      if (!isSignedIn) return;
      // No coalescing existed, so a slow check could overlap the next tick.
      //
      // NOTE: `timeoutMs` on checkFirebaseSession does NOT bound this guard. The
      // AbortController only aborts `fetch`; `getFreshAuthToken` runs first and makes its
      // own unabortable call to Clerk. A stalled token mint therefore holds this ref for
      // as long as the browser's socket timeout takes, during which eviction checks are
      // off for this tab. It always clears eventually, so this is degradation, not a
      // deadlock — but do not read the timeout as a guarantee.
      if (sessionCheckInFlightRef.current) return;
      sessionCheckInFlightRef.current = true;
      // Stamped HERE, after the guard, so a skipped check does not count as a check and
      // push the next visibility-triggered one out by a full interval.
      lastSessionCheckAtRef.current = Date.now();

      try {
        // NOTE: there is deliberately no `getToken({ skipCache: true })` here.
        // It used to run on every tick and its result was thrown away — and it could
        // not have helped anyway: that token comes from the @clerk/clerk-react provider,
        // while checkFirebaseSession goes through a SEPARATE @clerk/clerk-js instance in
        // useAuthedFetch with its own cache, which mints its own token regardless. It was
        // a forced network round-trip per tick that freshened nothing.
        const session = await checkFirebaseSession();

        // Only a DEFINITE negative signs the user out. The previous `!result.isValid`
        // over an `as any` meant any 200 lacking the field — a shape change, `{}`, a
        // `{ ok, data }` envelope — read as falsy and forced a real re-login.
        // Confirmed against the backend: GET /v1/clerk/check-session answers 200 with
        // `{ isValid: boolean, source }` (routes/clerk.ts), so a definite negative really
        // is how eviction is signalled — the modal is reachable.
        if (session?.isValid === false) {
          // Sign-out can land mid-flight; showing "you have been signed out" to someone
          // who just signed out themselves is noise.
          if (cancelled) return;
          toggleSignedOutModalOpen(true);
          captureEvent("multi_session_logout_popup");
        } else if (typeof session?.isValid !== "boolean") {
          // Narrowing to `=== false` removed the false-logout bug, but it also turns any
          // future shape change into a SILENTLY DEAD feature. Make that observable.
          Sentry.captureMessage(
            "check-session returned an unexpected shape",
            { level: "warning", tags: { area: "multi_session" } },
          );
        }
      } catch (error) {
        // Fails OPEN: a transport error leaves a genuinely-evicted user signed in. That
        // is the deliberate trade — a false logout is worse than a late one — but it does
        // mean this feature is best-effort, not a security control.
        Sentry.captureException(error);
      } finally {
        sessionCheckInFlightRef.current = false;
      }
    };

    // Check when the tab becomes visible, but not more than once per interval —
    // alt-tabbing used to fire an undebounced request every single time.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !isSignedIn) return;
      const sinceLast = Date.now() - lastSessionCheckAtRef.current;
      if (sinceLast < SESSION_CHECK_INTERVAL_MS) return;
      checkSession();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const interval = setInterval(() => {
      if (document.visibilityState === "visible" && isSignedIn) {
        checkSession();
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
    // `getToken` is intentionally NOT a dependency: Clerk does not guarantee a stable
    // identity for it, so including it tore down and re-established the interval on
    // unrelated renders. It is no longer used in this effect at all.
  }, [isSignedIn]);

  if (isMobileWeb && !isMobileWebAllowedPath(location.pathname)) {
    return <MobileWarning />;
  }

  return (
    <Suspense fallback={<p>Loading...</p>}>
      <RedemptionPresentationProvider
        value={setRedemptionPresentationBlocker}
      >
        {IS_E2E && <TestNavBridge />}
        <SentryRoutes>
          {/* Home at the basename root */}
          <Route
            index
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route
            path="/exam-outcome"
            element={
              <Suspense fallback={null}>
                <ExamOutcomePage />
              </Suspense>
            }
          />
          <Route
            path="/unsubscribe"
            element={
              <Suspense fallback={null}>
                <UnsubscribePage />
              </Suspense>
            }
          />
          <Route
            path="/organizations/:examType"
            element={<OrganizationExamPage />}
          />
          <Route path="/distribute/:token" element={<DistributionPage />} />
          <Route path="/redeem" element={<RedeemOrgLinkPage />} />
          <Route
            path="/redeem-success"
            element={
              <ProtectedRoute>
                <RedeemSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/refer/:code" element={<ReferralLandingPage />} />
          <Route
            path="study/*"
            element={
              <ProtectedRoute>
                <DakotaTestPrep />
              </ProtectedRoute>
            }
          />
          <Route
            path="test"
            element={
              <ProtectedRoute>
                <ExamScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="exam-result/:id"
            element={
              <ProtectedRoute>
                <ExamResultStandalone />
              </ProtectedRoute>
            }
          />
          <Route
            path="results"
            element={
              <ProtectedRoute>
                <ExamResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="past-exams"
            element={
              <ProtectedRoute>
                <PastExamsResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="perks"
            element={
              <ProtectedRoute>
                <PerksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="create-profile"
            element={
              <ProtectedRoute>
                <CreateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="create-profile-standalone"
            element={
              <ProtectedRoute>
                <CreateProfile standalone />
              </ProtectedRoute>
            }
          />
          <Route
            path="edit-profile"
            element={
              <ProtectedRoute>
                <EditProfileInfoRefactored />
              </ProtectedRoute>
            }
          />
          <Route
            path="paywall"
            element={
              <ProtectedRoute>
                <Paywall />
              </ProtectedRoute>
            }
          />
          <Route
            path="ai-paywall"
            element={
              APP_TO_BUILD === "electrical" || APP_TO_BUILD === "plumbing" ? (
                <ProtectedRoute>
                  <Paywall variant="ai" />
                </ProtectedRoute>
              ) : (
                <Navigate to="/paywall" replace />
              )
            }
          />
          <Route
            path="payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile/manage-payments"
            element={
              <ProtectedRoute>
                <ManagePayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile/notification-settings"
            element={
              <ProtectedRoute>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="referral"
            element={
              <ProtectedRoute>
                <ReferralScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exam-selection"
            element={
              canShowChangeExamType(APP_TO_BUILD) ? (
                <ProtectedRoute>
                  <ExamTypeSelection />
                </ProtectedRoute>
              ) : (
                <Navigate to="/study" replace />
              )
            }
          />
          <Route
            path="/force-purchase-state"
            element={
              <ProtectedRoute>
                <ForcePurchaseState />
              </ProtectedRoute>
            }
          />
          <Route
            path="exam-selection-standalone"
            element={
              canShowChangeExamType(APP_TO_BUILD) ? (
                <ProtectedRoute>
                  <ExamTypeSelection standalone />
                </ProtectedRoute>
              ) : (
                <Navigate to="/study" replace />
              )
            }
          />
          <Route
            path="ai-chat"
            element={
              <ProtectedRoute>
                <AIChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="question-guides"
            element={
              <ProtectedRoute>
                <QuestionGuidesPage />
              </ProtectedRoute>
            }
          />

          {/* Optional: 404 inside each basename */}
          <Route path="*" element={<p>{t("errors.not_found")}</p>} />
        </SentryRoutes>
        <Dialog
          open={signedOutModalOpen}
          onOpenChange={toggleSignedOutModalOpen}
        >
          <DialogPortal>
            <DialogContent
              onPointerDownOutside={(event) => event.preventDefault()}
              className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] border bg-background duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] w-[412px] p-6 bg-white rounded-lg shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] flex flex-col gap-4 [&>button]:hidden"
            >
              <DialogHeader>
                <DialogTitle className="flex !justify-center">
                  {t("auth.logged_out_title")}
                </DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-base flex !justify-center font-normal font-inter text-zinc-500 mt-1">
                {t("auth.logged_out_body")}
              </DialogDescription>
              <DialogFooter className="flex items-center !justify-center gap-2">
                <button
                  onClick={onLogout}
                  className="px-4 py-2 bg-[#5DB075] text-sm font-medium font-inter text-white hover:bg-[#4c9a64] transition-colors rounded-full"
                >
                  {t("common.continue")}
                </button>
              </DialogFooter>
            </DialogContent>
          </DialogPortal>
        </Dialog>
        <PendingAccessCodeStatusDialog
          status={statusDialogStatus}
          onDismiss={() => {
            redemptionAbandonedRef.current = true;
            setPendingAccessCodePresentation({ status: "idle" });
          }}
        />
        {pendingAccessCodePresentation.status === "error" && (
          <RedeemCodeModal
            examType={pendingAccessCodePresentation.pending.examType}
            open={!isRedemptionErrorDeferred}
            onOpenChange={(open) => {
              if (!open) {
                clearPendingAccessCode(
                  pendingAccessCodePresentation.pending,
                );
                setPendingAccessCodePresentation({ status: "idle" });
              }
            }}
            onLegacyRedeem={
              APP_TO_BUILD === "electrical_us"
                ? (code) => store.mainAppStore.redeemCode(code)
                : undefined
            }
            onRedeemed={handleRootCodeRedeemed}
            getBlockedReason={store.mainAppStore.getOrgRedeemBlockedReason}
            initialCode={pendingAccessCodePresentation.pending.code}
            initialErrorReason={pendingAccessCodePresentation.reason}
          />
        )}
        <Toaster />
      </RedemptionPresentationProvider>
    </Suspense>
  );
}

export default observer(App);
